import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, Divider, Box } from "@mui/material";
import SortableItem from "./SortableItem";
import useDroneForm from "./hooks/useDroneForm";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import StyleStack from "../../../../../Reuse/UI/StyleStack";
import { StyledTextInput } from "../../../../../Reuse/FormInput";
import { DroneBox } from "../../DroneBox";
import StyledButton from "../../../../../Reuse/UI/StyledButton";
import DroneRow from "./components/DroneRow";
import useHandleDragEnd from "./hooks/useHandleDragEnd";
import {
  useActiveCampusContext,
  useActiveDroneNestContext,
  useActiveFloorContext,
} from "../Constant";
import { setDroneNest } from "../../../../../Service/mappers";
import { CheckValue } from "../../../../../Service/service";
import {
  createDroneNest,
  deleteDroneNestById,
  getDroneNestById,
} from "../../../../../Service/Api/droneNest";
import { updateDroneNest } from "../../../../../Service/Api/droneNest/update";
import { useDebouncedApiCall } from "../../../../../hooks";
import { useAlerts, useDialog } from "../../../../../context";
import { useWatch } from "react-hook-form";
import DialogUi from "./DialogUi";
import useDronesList from "./hooks/useDronesList";
import useDoorsList from "./hooks/useDoorsList";
import DoorRow from "./components/DoorRow";

const DroneNestForm = ({ onSubmit }) => {
  const { call } = useDebouncedApiCall();
  const { showConfirm } = useDialog();
  const { showAlerts } = useAlerts();
  const { setActiveCampus, activeCampus } = useActiveCampusContext();
  const { activeFloor, setActiveFloorDroneNest, action, setAction } =
    useActiveFloorContext();
  const { activeDroneNest, setActiveDroneNest } = useActiveDroneNestContext();

  const { availableDronesList, setAvailableDrone, showAutomaticDrones } =
    useDronesList(activeDroneNest);

  const { setAvailableDoor, availableDoorsList, showAutomaticDoors } =
    useDoorsList(activeDroneNest);

  const [open, setOpen] = useState(null);

  const isLocal = useMemo(
    () => activeDroneNest?.id.includes("local_"),
    [activeDroneNest?.id],
  );

  const {
    control,
    register,
    handleSubmit,
    handleAddDrones,
    handleAddDoors,
    handleRemoveDoors,
    getValues,
    droneFields,
    moveDrones,
    updateDrones,
    setValue,
    doorFields,
    formState: { errors, isDirty },
  } = useDroneForm(activeDroneNest);

  const getList = useCallback(
    (status, id = null) => {
      if (status === "drones") {
        if (!!id) {
          return availableDronesList[id] || null;
        }
        return Object.values(availableDronesList);
      } else if (status === "doors") {
        if (!!id) {
          return availableDoorsList[id] || null;
        }
        return Object.values(availableDoorsList);
      } else {
        if (!!id) {
          return null;
        }
        return [];
      }
    },
    [availableDronesList, availableDoorsList],
  );

  useEffect(() => {
    const search = activeDroneNest?.id?.search("local_");
    if (search < 0) {
      setAction((prev) => ({ ...prev, isDirty }));
    }
  }, [isDirty, activeDroneNest?.id]);

  const handleDragEnd = useHandleDragEnd({
    droneFields,
    getValues,
    updateDrones,
    moveDrones,
  });

  const submitHandler = async (data) => {
    // Let Yup handle validation; do not manually filter
    const { name, display_id, base_station_ip, drones, doors, serverId } = data;
    const { campus_id, floor_id, latitude, longitude } = activeDroneNest;
    let dronesArr = [];
    drones.forEach((el) => {
      if (!!el.droneId) {
        dronesArr.push({
          campus_id,
          display_id: el.droneId,
          status: el.status,
          type: el.type,
          id: el.id,
          drone_ip: el.drone_ip,
          device_id: el.device_id,
          // base_station_ip: el.base_station_ip,
        });
      }
    });
    let capacity = Math.floor(dronesArr.length / 3) * 3;
    if (dronesArr.length % 3 > 0) {
      capacity = capacity + 3;
    }
    let formData = {
      campus_id,
      display_id,
      base_station_ip,
      drones: [...dronesArr],
      floor_id,
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      name,
      capacity,
      door: {},
    };
    if (doors.length) {
      doors.forEach((el) => {
        formData.door = {
          campus_id,
          display_id: el.display_id,
          state: el.status,
          port: Number(el.port),
          ip: el.ip,
          device_id: el.device_id,
        };
      });
    } else if (!!activeDroneNest?.doors?.id) {
      formData.deleted_door_id = activeDroneNest?.doors?.id || null;
    }

    try {
      const search = serverId.search("local_");
      if (search > -1) {
        const res = await call(() => createDroneNest(formData));
        setResponseData(res, "Drone box created successfully", serverId);
      } else {
        formData["drone_to_delete"] = [];
        activeDroneNest.drones.forEach((el) => {
          if (!formData.drones.filter((el1) => el1.id === el.id).length) {
            formData["drone_to_delete"].push(el.id);
          }
        });
        const res = await call(() => updateDroneNest(formData, serverId));
        setResponseData(res, "Drone box updated successfully", serverId);
      }
    } catch (error) {}
  };

  const setResponseData = useCallback(
    async (res, status, serverId = null) => {
      try {
        const resById = await call(() => getDroneNestById(res.data.id));
        if (status) showAlerts(status);
        const drone_nests = [...activeCampus.drone_nests].map((el) =>
          el.id === serverId ? resById.data : el,
        );
        setActiveFloorDroneNest(
          drone_nests.filter((el) => el.floor_id === activeFloor?.id),
        );

        let availableDrone = [];
        let unavailableDrone = [];

        const floors = [...activeCampus.floors];
        drone_nests.forEach((drone_nest) => {
          const matchingFloor = floors.find(
            (floor) => floor.id === drone_nest.floor_id,
          );
          if (matchingFloor) drone_nest.floor = matchingFloor;
          drone_nest.drones.forEach((drone) => {
            const entry = { ...drone, drone_nest: { ...drone_nest } };
            (drone.status === "AVAILABLE"
              ? availableDrone
              : unavailableDrone
            ).push(entry);
          });
        });

        setActiveCampus((prev) => ({
          ...prev,
          drone_nests,
          availableDrone,
          unavailableDrone,
        }));
        setActiveDroneNest(setDroneNest({}));
      } catch (error) {}
    },
    [
      activeCampus?.drone_nests,
      activeCampus?.floors,
      call,
      setActiveCampus,
      activeFloor?.id,
      setActiveDroneNest,
      showAlerts,
      setActiveFloorDroneNest,
    ],
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const cancelHandler = useCallback(
    async (e) => {
      if (action?.isDirty) {
        // create a popup to ask confirm you want to cancel this.
      }
      if (isLocal) {
        setActiveCampus((prev) => ({
          ...prev,
          drone_nests: prev.drone_nests.filter(
            (el) => el.id !== activeDroneNest?.id,
          ),
        }));
        setActiveFloorDroneNest((prev) =>
          prev.filter((el) => el.id !== activeDroneNest?.id),
        );
      }
      setActiveDroneNest(setDroneNest({}));
      setAction((prev) => ({
        ...prev,
        showOnMap: null,
      }));
    },
    [
      setAction,
      activeDroneNest,
      setActiveDroneNest,
      action,
      setActiveCampus,
      setActiveFloorDroneNest,
      isLocal,
    ],
  );
  const deleteHandler = useCallback(
    async (e) => {
      const handleDelete = async () => {
        try {
          if (!isLocal) {
            await call(() => deleteDroneNestById(activeDroneNest?.id));
          }

          setActiveCampus((prev) => ({
            ...prev,
            drone_nests: prev.drone_nests.filter(
              (el) => el.id !== activeDroneNest?.id,
            ),
          }));

          setActiveFloorDroneNest((prev) =>
            prev.filter((el) => el.id !== activeDroneNest?.id),
          );

          setActiveDroneNest(setDroneNest({}));
        } catch (error) {
          console.error(error);
        }
      };

      showConfirm(
        "Deleting this drone nest will also remove all drones deployed on it.",
        "Are you sure you want to proceed?",
        handleDelete,
      );
    },
    [
      activeDroneNest,
      setActiveDroneNest,
      setActiveCampus,
      isLocal,
      call,
      showConfirm,
      setActiveFloorDroneNest,
    ],
  );

  const openPopup = useCallback(
    (e, index, status) => {
      e?.preventDefault();
      setOpen({ index, status });
    },
    [setOpen],
  );

  const closePopup = useCallback(
    (e) => {
      e?.preventDefault();
      setOpen(null);
    },
    [setOpen],
  );

  const onDroneSelect = useCallback(
    (data) => {
      const { index } = open;
      const currentValues = getValues(`drones.${index}`);
      if (!currentValues.isUpdate && !!currentValues.id) {
        setAvailableDrone(currentValues.id, false);
      }
      setTimeout(() => {
        if (!!data["type"]) {
          setValue(`drones.${index}.type`, data["type"]);
        }
        if (!!data["display_id"]) {
          setValue(`drones.${index}.droneId`, data["display_id"]);
        }
        if (!!data["status"]) {
          setValue(`drones.${index}.status`, data["status"]);
        }
        if (!!data["drone_ip"]) {
          setValue(`drones.${index}.drone_ip`, data["drone_ip"]);
        }
        if (!!data["device_id"]) {
          setValue(`drones.${index}.device_id`, data["device_id"]);
        }
        setValue(`drones.${index}.id`, data["id"]);
        setValue(`drones.${index}.isUpdate`, false);
        setAvailableDrone(data.device_id, true);
      }, 500);
      closePopup();
    },
    [open, closePopup, setValue, getValues, setAvailableDrone],
  );
  const onDoorSelect = useCallback(
    (data) => {
      const { index } = open;
      const currentValues = getValues(`doors.${index}`);
      if (!!currentValues.id) {
        setAvailableDoor(currentValues.id, false);
      }
      setTimeout(() => {
        if (!!data["port"]) {
          setValue(`doors.${index}.port`, data["port"]);
        }
        if (!!data["display_id"]) {
          setValue(`doors.${index}.display_id`, data["display_id"]);
        }
        if (!!data["status"]) {
          setValue(`doors.${index}.status`, data["status"]);
        }
        if (!!data["ip"]) {
          setValue(`doors.${index}.ip`, data["ip"]);
        }
        if (!!data["device_id"]) {
          setValue(`doors.${index}.device_id`, data["device_id"]);
        }
        setValue(`doors.${index}.id`, data["id"]);
        setValue(`doors.${index}.isUpdate`, false);
        setAvailableDoor(data.device_id, true);
      }, 500);
      closePopup();
    },
    [open, closePopup, setValue, getValues, setAvailableDoor],
  );

  const onSelect = useCallback(
    (e, data) => {
      e?.preventDefault();
      const { status } = open;
      if (status === "doors") {
        onDoorSelect(data);
      } else if (status === "drones") {
        onDroneSelect(data);
      }
    },
    [onDoorSelect],
  );

  useEffect(() => {
    return () => {
      cancelHandler();
    };
  }, []);

  if (!activeDroneNest?.id) return null;

  return (
    <React.Fragment>
      <DialogUi
        open={CheckValue(open)}
        status={open?.status}
        list={getList(open?.status)}
        closePopup={closePopup}
        onSelect={onSelect}
      />
      <StyleStack
        elevation={0}
        gap={1}
        sx={{
          borderRadius: 1.5,
          width: 630,
          maxWidth: 630,
          mx: "auto",
          paddingY: 1,
        }}
      >
        <Stack
          direction={"row"}
          gap={1.5}
          alignItems="flex-start"
          sx={{ paddingX: 1.5, width: "100%" }}
        >
          <StyledTextInput
            label="Name"
            name="name"
            showError={false}
            control={control}
            placeholder={"Enter Name"}
          />
          <StyledTextInput
            label="Base Station IP"
            name="base_station_ip"
            showError={false}
            control={control}
            placeholder={"Enter Base Station IP"}
          />
          <StyledTextInput
            label="ID"
            name="display_id"
            showError={false}
            control={control}
            placeholder={"Enter Id"}
            sx={{ width: 64 }}
            readOnly={true}
          />
          <DroneBoxUI control={control} />
        </Stack>
        <Divider flexItem />
        <Stack
          direction={"column"}
          flexGrow={1}
          gap={2}
          sx={{ paddingX: 1.5, width: "100%" }}
        >
          {doorFields.length ? (
            doorFields
              .filter((field) => !!field.id)
              .map((field, index) => {
                return (
                  <DoorRow
                    index={index}
                    key={field.id}
                    field={field}
                    showAuto={showAutomaticDoors}
                    control={control}
                    openPopup={openPopup}
                    setValue={setValue}
                    setAvailableDoor={setAvailableDoor}
                    handleRemoveDoors={handleRemoveDoors}
                  />
                );
              })
          ) : (
            <StyledButton variant="outlined" onClick={handleAddDoors} fullWidth>
              Add Door
            </StyledButton>
          )}
        </Stack>

        <Divider flexItem />
        <Stack
          direction={"column"}
          flexGrow={1}
          gap={2}
          sx={{ paddingX: 1.5, overflow: "auto", width: "100%" }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={droneFields.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              {droneFields.map((field, index) => {
                const error =
                  !!errors?.drones && CheckValue(errors.drones[index])
                    ? errors.drones[index]
                    : null;

                const droneErrors =
                  (typeof errors?.drones?.message === "string" ||
                    typeof errors?.drones?.root?.message === "string") &&
                  index === 0
                    ? errors?.drones?.message || errors?.drones?.root?.message
                    : null;

                return (
                  <SortableItem
                    key={field.id}
                    index={index}
                    id={field.id}
                    error={error}
                    droneErrors={droneErrors}
                    control={control}
                  >
                    <DroneRow
                      index={index}
                      control={control}
                      setAvailableDrone={setAvailableDrone}
                      showAuto={showAutomaticDrones}
                      setValue={setValue}
                      register={register}
                      openPopup={openPopup}
                      error={error || !!droneErrors}
                    />
                  </SortableItem>
                );
              })}
            </SortableContext>
          </DndContext>
          <StyledButton variant="outlined" onClick={handleAddDrones} fullWidth>
            + Add 3 Drones
          </StyledButton>
        </Stack>

        <Divider flexItem />
        <Stack
          display="flex"
          flexDirection={"row"}
          justifyContent="flex-end"
          gap={1}
          sx={{ paddingX: 2, width: "100%" }}
        >
          {isLocal ? null : (
            <StyledButton
              variant="contained"
              color="error"
              onClick={deleteHandler}
            >
              Delete
            </StyledButton>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <StyledButton variant="outlined" onClick={cancelHandler}>
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            onClick={handleSubmit(submitHandler)}
          >
            Save
          </StyledButton>
        </Stack>
      </StyleStack>
    </React.Fragment>
  );
};

export default React.memo(DroneNestForm);

const DroneBoxUI = React.memo(({ control }) => {
  const drones = useWatch({ control, name: "drones" });
  const res = drones.filter(
    (d) => d.type?.trim() && d.status?.trim() && d.droneId?.trim(),
  );
  return (
    <DroneBox
      showAvailableEmpty={true}
      availableDrone={res ?? []}
      sx={{ alignSelf: "flex-end", marginBottom: 1 }}
    />
  );
});
